package navigation.home.service.allservice

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.*
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.input.key.Key
import androidx.compose.ui.input.key.KeyEventType
import androidx.compose.ui.input.key.key
import androidx.compose.ui.input.key.type
import androidx.compose.ui.unit.dp
import cafe.adriel.voyager.koin.koinScreenModel
import com.essntl.components.sidepanel.SidePanel
import com.essntl.components.sidepanel.SizeConstraint
import com.essntl.components.sidepanel.Splitter
import com.essntl.components.sidepanel.rememberSidePanelState
import com.essntl.features.service.domain.model.service.ServiceModel
import com.essntl.features.service.ui.addeditservice.AddEditServiceEvent
import com.essntl.features.service.ui.addeditservice.AddEditServiceScreen
import com.essntl.features.service.ui.allservice.AllServiceEvent
import com.essntl.features.service.ui.allservice.AllServiceNavigation
import com.essntl.features.service.ui.allservice.AllServiceScreen
import com.essntl.features.service.ui.components.ServiceHeaderSection
import essntl.key.LocalKeyEventManager
import com.essntl.uikit.components.dialog.EsAlertSaveDialog
import com.essntl.uikit.components.icons.EsIcons
import com.essntl.uikit.components.link.NavLinkState
import com.essntl.uikit.components.utils.EsConstants
import com.essntl.uikit.components.utils.spacing
import com.essntl.uikit.theme.esColorScheme
import com.mohamedrejeb.richeditor.model.rememberRichTextState
import kotlinx.coroutines.flow.collectLatest
import navigation.home.contact.ContactScreenModel
import navigation.home.service.addeditservice.AddEditServiceScreenModel
import navigation.home.suppliers.allsuppliers.AllSuppliersScreenModel
import navigation.utils.NavLinkScreen

object AllServiceScreenNav : NavLinkScreen {
    override val defaultTitle: String = "Services"

    override var navLinkState: NavLinkState by mutableStateOf(
        NavLinkState(
            title = defaultTitle,
            iconRes = EsIcons.Service,
        ),
    )

    @Composable
    override fun Content() {
        val allServiceScreenModel = koinScreenModel<AllServiceScreenModel>()
        val allServiceState by allServiceScreenModel.state.collectAsState()

        val addEditServiceScreenModel = koinScreenModel<AddEditServiceScreenModel>()
        val addEditServiceState by addEditServiceScreenModel.state.collectAsState()

        val allSupplierScreenModel = koinScreenModel<AllSuppliersScreenModel>()
        val allSupplierState by allSupplierScreenModel.state.collectAsState()

        val contactScreenModel = koinScreenModel<ContactScreenModel>()
        val contactState by contactScreenModel.state.collectAsState()

        val openAlertSaveServiceDialog = rememberSaveable { mutableStateOf(false) }

        val richTextState = rememberRichTextState()

        val keyEventManager = LocalKeyEventManager.current

        val sidePanelState =
            rememberSidePanelState(
                sidePanelVisible = false,
                panelSizeConstraint =
                    SizeConstraint(
                        minSize = 300.dp,
                        maxSize = 600.dp,
                    ),
            )

        LaunchedEffect(addEditServiceState.addEditService.isSuccess) {
            if (addEditServiceState.addEditService.isSuccess) {
                allServiceScreenModel.onEvent(AllServiceEvent.GetAllService())
                addEditServiceScreenModel.onEvent(AddEditServiceEvent.ClearSuccessState)
                if (allServiceState.selectedServiceId == null) {
                    addEditServiceScreenModel.onEvent(AddEditServiceEvent.ClearService)
                }
            }
        }

        LaunchedEffect(Unit) {
            allServiceScreenModel.onEvent(AllServiceEvent.GetAllService())
            navLinkState = navLinkState.copy(
                onRefresh = {
                    allServiceScreenModel.onEvent(AllServiceEvent.Refresh)
                    allServiceScreenModel.onEvent(AllServiceEvent.GetAllService())
                    sidePanelState.sidePanelOpened = false
                },
            )
        }

        LaunchedEffect(keyEventManager) {
            keyEventManager.events.collectLatest {
                if (it.key == Key.H && it.type == KeyEventType.KeyUp) {
                    sidePanelState.sidePanelOpened = false
                }
            }
        }

        /**
         * When side panel is closed, clear selected service
         */

        LaunchedEffect(sidePanelState.sidePanelOpened) {
            if (!sidePanelState.sidePanelOpened) {
                allServiceScreenModel.onEvent(AllServiceEvent.SelectService(null))
                addEditServiceScreenModel.onEvent(AddEditServiceEvent.ClearService)
            }
        }

        SidePanel(
            sidePanelState = sidePanelState,
            isHorizontal = true,
            sidePanel = {
                Column(
                    modifier =
                        Modifier
                            .fillMaxHeight()
                            .padding(end = 1.5f.spacing)
                            .clip(RoundedCornerShape(EsConstants.SectionBorderRadius))
                            .background(MaterialTheme.esColorScheme.bgSecondary),
                ) {
                    val isEdit by remember(addEditServiceState.service.value.id) {
                        mutableStateOf(
                            addEditServiceState.service.value.id.isNotBlank(),
                        )
                    }
                    ServiceHeaderSection(
                        title = if (isEdit) {
                            addEditServiceState.service.value.title
                        } else {
                            "Creating new service"
                        },
                        referenceCode = addEditServiceState.service.value.referenceCode,
                        onArrowClick = {
                            if (addEditServiceState.isEditMode && addEditServiceState.hasChanges) {
                                openAlertSaveServiceDialog.value = true
                            } else {
                                sidePanelState.sidePanelOpened = false
                            }
                        },
                        modifier = Modifier.padding(2.5f.spacing),
                    )

                    AddEditServiceScreen(
                        state = addEditServiceState,
                        onEvent = addEditServiceScreenModel::onEvent,
                        validation = addEditServiceScreenModel.validation,
                        contactState = contactState,
                        contactEvent = contactScreenModel::onEvent,
                        richTextState = richTextState,
                    )

                    if (openAlertSaveServiceDialog.value) {
                        EsAlertSaveDialog(
                            title = "service",
                            onDismissRequest = {
                                openAlertSaveServiceDialog.value = false
                            },
                            onReturn = {
                                sidePanelState.sidePanelOpened = false
                            },
                        )
                    }
                }
            },
            splitter =
                Splitter(
                    measuredPart = {
                        Box(
                            modifier =
                                Modifier
                                    .width(1.spacing)
                                    .fillMaxHeight(),
                        )
                    },
                ),
            modifier =
                Modifier
                    .fillMaxSize(),
        ) {
            AllServiceScreen(
                state = allServiceState,
                onEvent = allServiceScreenModel::onEvent,
                allSuppliersState = allSupplierState,
                onEventAllSupplier = allSupplierScreenModel::onEvent,
                isSidePanelOpen = sidePanelState.sidePanelOpened,
                onNavigation = {
                    when (it) {
                        is AllServiceNavigation.AddService -> {
                            allServiceScreenModel.onEvent(AllServiceEvent.SelectService(null))
                            addEditServiceScreenModel.onEvent(AddEditServiceEvent.ClearService)
                            addEditServiceScreenModel.onEvent(
                                AddEditServiceEvent.UpdateServiceFields(
                                    ServiceModel.empty.copy(
                                        category = it.categoryId,
                                    ),
                                ),
                            )
                            addEditServiceScreenModel.onEvent(
                                AddEditServiceEvent.ClearImages,
                            )
                            sidePanelState.sidePanelOpened = true
                        }

                        is AllServiceNavigation.EditService -> {
                            allServiceScreenModel.onEvent(AllServiceEvent.SelectService(it.id))
                            addEditServiceScreenModel.onEvent(AddEditServiceEvent.ClearService)
                            addEditServiceScreenModel.onEvent(AddEditServiceEvent.GetService(it.id))
                            sidePanelState.sidePanelOpened = true
                        }
                    }
                },
            )
        }
    }
}